// @flow
import createReducer from '../utils/createReducer';
import {pick, isEqual} from 'lodash';
import {
    instantBookFilter,
    newArrayFromFilters,
    getLengthOfStay
} from '../utils/filters';
import type {Action as SearchParamsActions} from '../actions/searchParams';
import {ResultsParams} from '../../server/graphql/params';
import {ROOT_PATH} from '@homeaway/search-url-parser';

import {
    MIN_ADULTS,
    MIN_BEDROOMS,
    MAX_BEDROOMS,
    MIN_BATHROOMS,
    MAX_BATHROOMS,
    MIN_PRICE,
    MAX_PRICES
} from '../utils/constants';
import {
    enforceMinimumRange,
    enforceMaximumRange,
    paramsHasMinFieldorMaxField
} from '../utils/enforceValues';

import type {ModalType} from '../actions/modals';

// `SearchParamsType` type extends from @homeaway/search-url-parser
// TODO: re-evaluate domain terminology `SearchParamsType` w.r.t. graphQL
export type SearchParamsType = {
    adults?: any, // flow has issue with adults as a `number` type?  Strange issue...
    adultsCount?: number,
    arrival?: string,
    ascending?: boolean,
    children?: number,
    childrenCount?: number,
    currency?: string,
    departure?: string,
    fetchImages?: any, // UI specific
    filter?: string[],
    filterByTotalPrice?: boolean,
    filterVersion?: string,
    legacyRegionPath?: string,
    locale?: string,
    maxBathrooms?: ?number, // customized to allow `null` values
    maxBedrooms?: ?number, // customized to allow `null` values
    maxNightlyPrice?: ?number, // customized to allow `null` values
    maxPrice?: ?number, // deprecated; use maxNightlyPrice instead
    maxTotalPrice?: ?number, // customized to allow `null` values
    maxSleeps?: number,
    minBathrooms?: number,
    minBedrooms?: number,
    minPrice?: ?number, // deprecated; use minNightlyPrice instead
    minNightlyPrice?: number,
    minTotalPrice?: ?number,
    minSleeps?: number,
    mock?: string,
    modal?: ModalType | ModalType[],
    neLat?: number,
    neLong?: number,
    orderBy?: string,
    page?: number,
    pageSize?: number,
    petIncluded?: boolean,
    pinnedPropertyListing?: string,
    q?: string,
    regionId?: number | string, // customized to allow string potentially
    searchTermUuid?: string,
    ssr?: boolean,
    swLat?: number,
    swLong?: number,
    view?: string,
    zoom?: number,
    isBoundingBoxSearch?: boolean,
    isSameDaySelected?: boolean
};

export type SearchRequestType = {
    q?: string,
    lbsUuid?: string,
    dates?: {
        arrivalDate: string,
        departureDate: string
    },
    currency?: string,
    filters?: string[],
    coreFilters?: {
        adults?: number,
        children?: number,
        pets?: number,
        minBedrooms?: number,
        maxBedrooms?: number,
        minSleeps?: number,
        maxSleeps?: number,
        minNightlyPrice?: number,
        maxNightlyPrice?: number,
        minTotalPrice?: number,
        maxTotalPrice?: number,
        minBathrooms?: number,
        maxBathrooms?: number
    },
    visitorInformation?: {
        havId: string,
        hasId: string
    },
    pinnedPropertyListing?: string,
    paging: {
        page: number,
        pageSize: number
    }
};

export type SearchParam = $Keys<SearchParamsType>;

// `State` contains additional fields
export type State = {
    +pathname: string,
    +params: SearchParamsType,
    +prevParams: SearchParamsType, // params from the last search
    +inputParams: SearchParamsType, // UI specific state for editing filters, etc. NOTE: the ids in filter aren't
    // necessarily the same as params.filter since search can return a different set of ids
    // to represent the same set of filters. e.g. we could pass in filter=Property+Type:apartment and search
    // responds back with filter=filter:67  :(
    +numFilters?: number,
    +numFiltersPanel?: number,
    +inputParamsAreDefault: boolean,
    +inputParamsAreChanged: boolean,
    +inputParamsAreUpdating: boolean,
    +priceInputValuesAreValid: boolean,
    +selectedFilters: string[],
    +isBoundingBoxSearch?: boolean
};

// `defaultParams` are default sane values for this reducer's state
export const defaultParams = {
    filter: [],
    filterByTotalPrice: false,
    minBedrooms: 0,
    maxBedrooms: null,
    minBathrooms: 0,
    maxBathrooms: null,
    minNightlyPrice: 0,
    maxNightlyPrice: null,
    minTotalPrice: null,
    maxTotalPrice: null,
    petIncluded: false,
    ssr: true
};

// Initial state when first initiailzing params
const initialState: State = {
    pathname: ROOT_PATH,
    params: defaultParams,
    prevParams: defaultParams,
    inputParams: defaultParams,
    inputParamsAreDefault: false,
    inputParamsAreChanged: false,
    inputParamsAreUpdating: false,
    priceInputValuesAreValid: true,
    selectedFilters: []
};

const preparedParams = (params: SearchParamsType) =>
    pick(params, ResultsParams);

const countCheckBoxFilters = (filter): number => {
    return (filter && filter.length) || 0;
};

const getBathroomCount = (params: Object): number => {
    return paramsHasMinFieldorMaxField(params, 'minBathrooms', 'maxBathrooms');
};

/**
 * countMoreFilters
 * count the number of filters applied to the more filters dropdown
 * @param {*} params
 */
const countMoreFilters = (params: Object): number => {
    let numFiltersPanel =
        getBathroomCount(params) + countCheckBoxFilters(params.filter);
    let instantBookCount = instantBookFilter(params.filter) ? 1 : 0;
    return numFiltersPanel - instantBookCount;
};

/**
 * countUniqueFilters()
 * Does simple computation of number of filters given param state
 * numFiltersPanel counts should only contains number of filters that is in more filters (desktop)
 * @param {*} params
 */
const countUniqueFilters = (params: Object): number => {
    let numNonCheckBoxFilters = getBathroomCount(params);
    numNonCheckBoxFilters += countCheckBoxFilters(params.filter);

    numNonCheckBoxFilters +=
        params.minBedrooms === 0 && params.maxBedrooms === 0 // studio filter
            ? 1
            : paramsHasMinFieldorMaxField(params, 'minBedrooms', 'maxBedrooms');

    numNonCheckBoxFilters += paramsHasMinFieldorMaxField(
        params,
        'minNightlyPrice',
        'maxNightlyPrice'
    );
    numNonCheckBoxFilters += paramsHasMinFieldorMaxField(
        params,
        'minTotalPrice',
        'maxTotalPrice'
    );

    return numNonCheckBoxFilters;
};

/**
 * inputParamsAreChanged()
 * Canonicalizes both `inputParams` with JSON.stringify() and returns true if they are not equal
 * @param {*} state
 */
function inputParamsAreChanged(state, newState) {
    // when considering whether inputParams has changed, we need to consider all params such as dates and guests.
    return !isEqual(
        JSON.stringify(preparedParams(state.inputParams)),
        JSON.stringify(preparedParams(newState.inputParams))
    );
}

/**
 * inputParamsAreChanged()
 * Canonicalizes `inputParams` and `defaultParams` with JSON.stringify() and returns true if they are equal
 * @param {*} state
 */
function inputParamsAreDefault(state) {
    // The "default" params don't include dates or guests
    return isEqual(
        JSON.stringify(pick(state.inputParams, Object.keys(defaultParams))),
        JSON.stringify(pick(defaultParams, Object.keys(defaultParams)))
    );
}

const actionHandlers = {
    ['SEARCH_PARAMS/SET'](state, {payload}: SearchParamsActions) {
        const {pathname, params, currency} = payload;
        const MAX_PRICE = MAX_PRICES[currency.toUpperCase()];

        let newParams = {
            ...initialState.params,
            ...params
        };

        // check that minBedrooms and minBathrooms is in a valid range
        enforceMinimumRange(
            newParams,
            'minBedrooms',
            MIN_BEDROOMS,
            MAX_BEDROOMS
        );
        enforceMinimumRange(
            newParams,
            'minBathrooms',
            MIN_BATHROOMS,
            MAX_BATHROOMS
        );

        /* If maxBedrooms is defined but it outside the range of 0 to 50
        or its less than the minBedrooms then delete it, same for minBathrooms */
        enforceMaximumRange(
            newParams,
            'minBedrooms',
            'maxBedrooms',
            MIN_BEDROOMS,
            MAX_BEDROOMS
        );
        enforceMaximumRange(
            newParams,
            'minBathrooms',
            'maxBathrooms',
            MIN_BATHROOMS,
            MAX_BATHROOMS
        );

        // Check Nightly Price Ranges
        enforceMinimumRange(newParams, 'minNightlyPrice', MIN_PRICE, MAX_PRICE);

        enforceMaximumRange(
            newParams,
            'minNightlyPrice',
            'maxNightlyPrice',
            MIN_PRICE,
            MAX_PRICE
        );

        // Check Total Price Ranges
        if (newParams.minTotalPrice || newParams.maxTotalPrice) {
            const {arrival, departure} = newParams;
            const lengthOfStay = getLengthOfStay(arrival, departure);

            enforceMinimumRange(
                newParams,
                'minTotalPrice',
                MIN_PRICE,
                MAX_PRICE * lengthOfStay
            );

            enforceMaximumRange(
                newParams,
                'minTotalPrice',
                'maxTotalPrice',
                MIN_PRICE,
                MAX_PRICE * lengthOfStay
            );
        }

        // Check Adults min value without upper bound.
        enforceMinimumRange(
            newParams,
            'adultsCount',
            MIN_ADULTS,
            Number.MAX_VALUE
        );

        let newState = {
            ...state,
            prevParams: {...state.params},
            params: newParams,
            pathname
        };

        if (
            !isEqual(preparedParams(state.params), preparedParams(newParams)) ||
            state.params.view !== newParams.view
        ) {
            newState = {
                ...newState,
                inputParams: {...newParams}, // Initialize copy
                inputParamsAreChanged: false,
                inputParamsAreUpdating: false
            };
        }

        newState.inputParamsAreDefault = inputParamsAreDefault(newState);

        // if the filters aren't being updated as part of the action, don't blow away inputParams.filter
        // This is to prevent non-filter navigation from blowing away the updated filter ids from search
        if (
            state.params &&
            newArrayFromFilters(state.params).join('|') ===
                newArrayFromFilters(newState.params).join('|') &&
            state.inputParams.filter
        ) {
            newState.inputParams.filter = state.inputParams.filter;
        }

        newState.numFilters = countUniqueFilters(newState.inputParams);
        newState.isBoundingBoxSearch = !!(
            newState.inputParams.neLat &&
            newState.inputParams.neLong &&
            newState.inputParams.swLat &&
            newState.inputParams.swLong
        );

        return newState;
    },
    ['SEARCH_PARAMS/SYNC_INPUT_PARAMS'](state, {payload}: SearchParamsActions) {
        // Map Payload
        const newState = {
            ...state
        };

        // Computed Values
        newState.inputParams = {...newState.params}; // Make a copy of `state.params`
        newState.inputParamsAreChanged = false;
        newState.inputParamsAreDefault = inputParamsAreDefault(newState);

        // sync payload filter
        if (payload && payload.filter) {
            newState.selectedFilters = [].concat(payload.filter);
        }

        // sync input params with selected fitlers
        newState.inputParams.filter = [].concat(newState.selectedFilters || []);

        // update the count
        newState.numFilters = countUniqueFilters(newState.inputParams);
        newState.numFiltersPanel = countMoreFilters(newState.inputParams);
        newState.priceInputValuesAreValid = true;

        return newState;
    },
    ['SEARCH_PARAMS/UPDATE_INPUT_PARAMS_COMPLETE'](
        state,
        {}: SearchParamsActions
    ) {
        const newState = {
            ...state,
            inputParamsAreUpdating: false
        };

        return newState;
    },
    ['SEARCH_PARAMS/UPDATE_INPUT_PARAMS_REQUEST'](
        state,
        {payload}: SearchParamsActions
    ) {
        const newState = {
            ...state,
            inputParams: {
                ...state.inputParams,
                ...payload
            }
        };

        // Computed Values
        newState.inputParamsAreUpdating = true;
        newState.inputParamsAreChanged = inputParamsAreChanged(state, newState);
        newState.inputParamsAreDefault = inputParamsAreDefault(newState);

        newState.numFiltersPanel = countMoreFilters(newState.inputParams);

        if (
            payload.minNightlyPrice ||
            payload.maxNightlyPrice ||
            payload.minTotalPrice ||
            payload.maxTotalPrice
        ) {
            newState.priceInputValuesAreValid = true;
        }

        return newState;
    },
    ['SEARCH_PARAMS/RESET_INPUT_PARAMS'](state, {payload}) {
        const paths = payload || Object.keys(defaultParams);
        // Map payload
        const newState = {
            ...state,
            inputParams: {
                ...state.inputParams,
                ...pick(defaultParams, paths)
            }
        };

        // Computed Values
        newState.inputParamsAreUpdating = false;
        newState.inputParamsAreChanged = inputParamsAreChanged(state, newState);
        newState.inputParamsAreDefault = inputParamsAreDefault(newState);

        if (
            paths.indexOf('minNightlyPrice') > -1 ||
            paths.indexOf('maxNightlyPrice') > -1 ||
            paths.indexOf('minTotalPrice') > -1 ||
            paths.indexOf('maxTotalPrice') > -1
        ) {
            newState.priceInputValuesAreValid = true;
        }
        return newState;
    },
    ['SEARCH_PARAMS/UPDATE_PRICE_INPUT_VALUES_VALIDITY'](state, {payload}) {
        return {
            ...state,
            priceInputValuesAreValid: payload.areValid
        };
    }
};

export default createReducer(actionHandlers, initialState);
